import { mdiHistory, mdiSortAscending, mdiSortDescending } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { useEffect, useState } from "react";
import { Tooltip } from "@material-ui/core";
import Pagination from "../../ui/new_components/common/pagination";
import { FieldType } from "./create_view/form_field";

export interface NewTableHeaderProps {
  title?: string;
  colspan?: number;
  rowspan?: number;
  align?: "left" | "right" | "center" | "justify" | "char";
  type: FieldType;
  key: string;
  sort_order?: "asc" | "desc";
  onClick?: (data: { sort: string; sort_order: string }) => void;
  value?: string;
  stickyColumn?: boolean;
  left?: string | number;
  right?: string | number;
  builder?: () => JSX.Element;
}

export interface NewTableRow<T> {
  stickyRow?: boolean;
  top?: string | number;
  bottom?: string | number;
  data: T;
  onClick?: (data: T) => void;
  cells: {
    maxLines?: number;
    title?: string;
    colspan?: number;
    rowspan?: number;
    value?: string | number | Date;
    align?: "left" | "right" | "center" | "justify" | "char";
    builder?: () => JSX.Element;
  }[];
}

interface NewTableViewProps<T extends { id?: string }> {
  show_index?: boolean;
  loading?: boolean;
  show_pagination?: boolean;
  lazy_loading?: boolean;
  infinite_Scroll?: boolean;
  page?: number;
  total?: number;
  count?: number;
  itemCount?: number;
  onShowHistory: (data: T) => void;
  onPageChange?: (p: number) => void;
  headers: NewTableHeaderProps[];
  stickyHeader?: boolean;
  rows: NewTableRow<T>[];
  mobileCard?: boolean;
  cardTitleIndex?: number;
  cardStatusIndex?: number;
  cardHideIndexes?: number[];
}

const NewTableView = <T extends { id?: string }>({
  headers,
  rows,
  page,
  count,
  itemCount,
  total,
  stickyHeader,
  onPageChange,
  onShowHistory,
  loading,
  show_pagination,
  lazy_loading,
  infinite_Scroll,
  show_index,
  mobileCard,
  cardTitleIndex = 0,
  cardStatusIndex,
  cardHideIndexes,
}: NewTableViewProps<T>): JSX.Element => {
  const [offSet, setOffSet] = useState(0);

  useEffect(() => {
    const container = document.getElementById("page_container");
    const top_section = document.getElementById("top_section");
    const table = document.getElementById("table-container");
    if (container && top_section && table) {
      table.style.height = `${
        container.clientHeight - 16 - top_section.clientHeight
      }px`;
    }
    if (page && count) setOffSet(page > 0 ? (page - 1) * count : 0);
  }, [page, count]);

  const formatCellValue = (cell: any, fieldType: FieldType) => {
    if (cell.value === null || cell.value === undefined) {
      return "--";
    }

    switch (fieldType) {
      case FieldType.date:
        if (
          (typeof cell.value === "string" && cell.value.includes("-")) ||
          cell.value instanceof Date
        ) {
          return moment(cell.value, moment.ISO_8601, true).isValid()
            ? moment(cell.value).format("DD-MM-YYYY")
            : cell.value;
        }
        return cell.value;

      case FieldType.number:
        return cell.value ?? 0;

      case FieldType.currency:
        return cell.value ?? "--";

      default:
        return cell.value ?? "--";
    }
  };

  const renderCardCell = (cell: any, header?: NewTableHeaderProps) => {
    if (cell?.builder) return cell.builder();
    if (!header) return cell?.value ?? "--";
    return formatCellValue(cell, header.type);
  };

  const renderTableCell = (
    cell: any,
    header: NewTableHeaderProps,
    rowIndex: number,
    cellIndex: number,
    row: NewTableRow<T>,
  ) => {
    const commonStyles = {
      ...(header.stickyColumn
        ? { left: header.left, right: header.right }
        : {}),
    };

    const commonClasses = `
      ${(header.stickyColumn || row.stickyRow) && "sticky"} 
      p-1 sticky-field max-w-xs px-5 border-r-2 overflow-hidden overflow-ellipsis
      ${row.onClick ? "cursor-pointer" : ""}
    `;

    if (cell.builder) {
      return (
        <td
          style={commonStyles}
          title={cell.title}
          align={cell.align}
          colSpan={cell.colspan || 1}
          rowSpan={cell.rowspan || 1}
          key={cellIndex}
          className={commonClasses}
        >
          {cell.builder()}
        </td>
      );
    }

    return (
      <td
        style={commonStyles}
        title={cell.title}
        align={cell.align}
        colSpan={cell.colspan || 1}
        rowSpan={cell.rowspan || 1}
        key={cellIndex}
        className={`${commonClasses} text-left`}
      >
        <div
          style={{
            overflow: "hidden",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: cell.maxLines,
          }}
          className={`overflow-ellipsis text-left`}
        >
          {formatCellValue(cell, header.type)}
        </div>
      </td>
    );
  };

  return (
    <section
      id="table-container"
      className="flex flex-col justify-between items-center w-full bg-white border rounded-lg"
    >
      <div
        id="scroll_listener"
        className={`overflow-auto h-full w-full ${
          mobileCard ? "hidden md:block" : ""
        }`}
        style={{ height: "95%" }}
      >
        <table className="divide-y divide-gray-200 w-full">
          <thead className={`${stickyHeader && "sticky -top-1"} z-10`}>
            <tr
              className={`${
                stickyHeader && "sticky -top-0.5"
              } text-xs bg-white shadow whitespace-nowrap rounded-lg`}
            >
              {show_index && (
                <th className="mx-5 pb-2 font-semibold px-5 border-r-2 w-8">
                  <div className="py-1">Sl</div>
                </th>
              )}

              {headers.map((header, i) =>
                header.builder ? (
                  header.builder()
                ) : (
                  <th
                    align={header.align}
                    colSpan={header.colspan || 1}
                    rowSpan={header.rowspan || 1}
                    style={
                      header.stickyColumn
                        ? { left: header.left, right: header.right }
                        : {}
                    }
                    title={header.title}
                    className={`${
                      header.stickyColumn && "sticky"
                    } pb-2 font-semibold px-5 border-r-2 overflow-hidden`}
                    key={i}
                  >
                    <div className="p-1 flex justify-between items-center gap-1">
                      <div
                        title={header.title}
                        className={`flex flex-col gap-5 ${
                          header.align === "center"
                            ? "text-center"
                            : header.align === "right"
                              ? "text-right"
                              : "text-left"
                        }`}
                      >
                        <h3>{header.value ?? "---"}</h3>
                      </div>
                      {header.onClick && (
                        <div
                          onClick={() => {
                            header.onClick?.({
                              sort: header.key,
                              sort_order:
                                header.sort_order === "asc" ? "desc" : "asc",
                            });
                          }}
                          title={
                            header.sort_order === "asc"
                              ? "Ascending"
                              : "Descending"
                          }
                        >
                          <Icon
                            path={
                              header.sort_order === "asc"
                                ? mdiSortAscending
                                : mdiSortDescending
                            }
                            className="h-4 w-5"
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ),
              )}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 max-h-96 mx-2 text-xs items-start">
            {rows.length > 0 ? (
              rows.map((row, i) => (
                <tr
                  style={
                    row.stickyRow ? { top: row.top, bottom: row.bottom } : {}
                  }
                  onClick={
                    row.onClick ? () => row.onClick!(row.data) : undefined
                  }
                  key={i}
                  className={`sticky-row ${row.stickyRow && "sticky"} ${
                    row.onClick ? "cursor-pointer" : ""
                  } bg-white shadow p-1 hover:bg-gray-200`}
                >
                  <td className="pt-3 px-5 border-r-2">
                    <Tooltip title="History">
                      <svg
                        onClick={() => onShowHistory(row.data)}
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 cursor-pointer hover:text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </Tooltip>
                  </td>

                  {row.cells.map((cell, j) =>
                    renderTableCell(cell, headers[j], i, j, row),
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length + (show_index ? 1 : 0)}>
                  <div className="h-full w-full flex justify-center items-center">
                    <div className="p-10">No Records Found!</div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {mobileCard && (
        <div
          className="md:hidden w-full flex flex-col gap-3 p-2 overflow-y-auto"
          style={{ height: "95%" }}
        >
          {rows.length > 0 ? (
            rows.map((row, i) => {
              const titleCell = row.cells[cardTitleIndex];
              const statusCell =
                cardStatusIndex !== undefined
                  ? row.cells[cardStatusIndex]
                  : undefined;
              return (
                <div
                  key={i}
                  onClick={
                    row.onClick ? () => row.onClick!(row.data) : undefined
                  }
                  className={`rounded-lg border border-gray-300 shadow-sm bg-white p-3 text-sm ${
                    row.onClick ? "cursor-pointer" : ""
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0 break-all font-semibold text-blue-500">
                      {titleCell
                        ? renderCardCell(titleCell, headers[cardTitleIndex])
                        : "--"}
                    </div>
                    {statusCell && (
                      <div className="shrink-0">
                        {renderCardCell(statusCell, headers[cardStatusIndex!])}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex flex-col gap-1 text-xs text-gray-600">
                    {row.cells.map((cell, j) => {
                      if (j === cardTitleIndex || j === cardStatusIndex)
                        return null;
                      if (cardHideIndexes?.includes(j)) return null;
                      return (
                        <div key={j} className="flex justify-between gap-2">
                          <span className="text-gray-400">
                            {headers[j]?.value ?? headers[j]?.title ?? ""}
                          </span>
                          <span className="font-medium text-gray-700 text-right break-words">
                            {renderCardCell(cell, headers[j])}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 flex items-center justify-end gap-3 border-t pt-2">
                    <button
                      title="History"
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowHistory(row.data);
                      }}
                      className="text-gray-500 hover:text-myPrimaryColor"
                    >
                      <Icon path={mdiHistory} className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-10 text-center text-gray-400">
              No Records Found!
            </div>
          )}
        </div>
      )}

      {show_pagination && (
        <div className="px-1 w-full">
          <Pagination
            page={page ?? 1}
            total={total || 0}
            count={count ?? 0}
            itemCount={itemCount || 0}
            onChange={onPageChange ? onPageChange : () => {}}
          />
        </div>
      )}
    </section>
  );
};

export default NewTableView;
